import React, { useState, useEffect, useRef } from 'react';
import ProductFilter from './ProductFilter';
import ComparisonTable from './ComparisonTable';
import AdvancedFilters from './AdvancedFilters';
import './CompareApp.css';
import { useProductsWithStagedLoading } from '../hooks/useProductsWithStagedLoading';
import { useSelectedProducts } from '../hooks/useSelectedProducts';
import { useProductFilter } from '../hooks/useProductFilter';
import { useGraphQLTimeout } from '../hooks/useGraphQLTimeout';
import { debug, warn } from '../utils/debug';

interface CompareAppProps {
  initialSelectedProductIds?: string[];
  maxProducts?: number;
  collectionHandle?: string;
}

/**
 * Main component for the product comparison tool - simplified version
 */
const CompareApp: React.FC<CompareAppProps> = ({
  initialSelectedProductIds = [],
  maxProducts = 3,
  collectionHandle = 'all'
}) => {
  // State for showing advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
  const [filterKey, setFilterKey] = useState<number>(0); // Key to force remount of filters
  const [error, setError] = useState<string | null>(null);
  // We use a ref to store the reset button element
  const resetButtonRef = useRef<HTMLButtonElement>(null);

  // Get products data with three-stage loading
  const {
    initialProducts,
    initialShopifyProducts,
    isInitialLoading,
    intermediateProducts,
    intermediateShopifyProducts,
    isIntermediateLoading,
    allProducts,
    allShopifyProducts,
    isAllProductsLoading,
    error: productsError
  } = useProductsWithStagedLoading(collectionHandle);

  // Progressive enhancement: use best available data
  // Stage 1: Initial products for immediate display
  // Stage 2: Intermediate products for filtering (preferred over initial)
  // Stage 3: All products with full data when available (best)
  const availableProducts = allProducts.length > 0 
    ? allProducts 
    : intermediateProducts.length > 0 
      ? intermediateProducts 
      : initialProducts;
      
  const shopifyProductsData = allShopifyProducts.length > 0 
    ? allShopifyProducts 
    : intermediateShopifyProducts.length > 0 
      ? intermediateShopifyProducts 
      : initialShopifyProducts;
      
  const isLoading = allProducts.length === 0 
    ? intermediateProducts.length === 0 
      ? isInitialLoading 
      : isIntermediateLoading
    : isAllProductsLoading;

  // Monitor for long-running queries for diagnostic purposes
  const timeoutWarning = useGraphQLTimeout(isLoading);

  // Process selected products
  const {
    selectedProductIds,
    setSelectedProductIds,
    productData
  } = useSelectedProducts(initialSelectedProductIds, maxProducts, shopifyProductsData);

  // Remove auto-selection - let users choose products manually

  // Handle product filtering
  const { handleFilterChange, getFilteredProducts, getReplacementProducts, resetFilters } = useProductFilter();

  // Process errors
  useEffect(() => {
    if (productsError) {
      console.error('Product loading error:', productsError);
      setError(`Error loading products: ${productsError.message}`);
    } else if (timeoutWarning) {
      // For timeout, just log a warning but don't set an error that would block the UI
      warn('GraphQL timeout warning:', timeoutWarning);
    } else {
      setError(null);
    }
  }, [productsError, timeoutWarning]);

  // Filter the available products
  const filteredAvailableProducts = getFilteredProducts(availableProducts);

  /**
   * AIDEV-NOTE: Auto-replacement of filtered-out products
   * This effect watches for changes in the filtered products list.
   * When a selected product is no longer available in the filtered products (e.g., because
   * filters were applied that exclude it), we automatically select a different product from
   * the filtered list to maintain the comparison experience.
   * 
   * The replacement logic is implemented in the useProductFilter hook's getReplacementProducts
   * function, which tries to find a suitable replacement product that isn't already selected.
   */
  const [lastFilteredProductsLength, setLastFilteredProductsLength] = useState<number>(0);

  useEffect(() => {
    // Only run auto-replacement when filters change (not when selection changes)
    // This prevents infinite loops where replacement triggers more replacements
    if (filteredAvailableProducts.length !== lastFilteredProductsLength) {
      setLastFilteredProductsLength(filteredAvailableProducts.length);

      // Only attempt auto-replacement if:
      // 1. We have selected products
      // 2. We have filtered products to choose from
      // 3. We have enough filtered products to replace what's needed
      if (
        selectedProductIds.length > 0 &&
        filteredAvailableProducts.length > 0 &&
        // Make sure we have enough products to potentially replace all selected ones
        filteredAvailableProducts.length >= selectedProductIds.length
      ) {
        // Check if any selected products are no longer in the filtered list
        const updatedSelection = getReplacementProducts(selectedProductIds, filteredAvailableProducts);

        // If we have replacements, update the selection
        if (updatedSelection !== selectedProductIds) {
          debug('Auto-replacing filtered-out products:', {
            previous: selectedProductIds,
            updated: updatedSelection
          });
          setSelectedProductIds(updatedSelection);
        }
      }
    }
  }, [filteredAvailableProducts, lastFilteredProductsLength, selectedProductIds, getReplacementProducts, setSelectedProductIds]);

  debug("availableProducts", availableProducts);

  // Effect to measure shop-now-small button width and apply it to reset button
  useEffect(() => {
    const measureButtonWidth = () => {
      // Find the first shop-now-small button in the document
      const shopNowButton = document.querySelector('.shop-now-small') as HTMLElement;

      if (shopNowButton && resetButtonRef.current) {
        // Get the width of the shop-now-small button
        const width = shopNowButton.offsetWidth;

        // Apply the same width to the reset button
        debug('Setting reset button width to:', width);
        resetButtonRef.current.style.width = `${width}px`;
      }
    };

    // Measure after a short delay to ensure components are rendered
    const timeoutId = setTimeout(measureButtonWidth, 50);

    // Also measure on window resize
    window.addEventListener('resize', measureButtonWidth);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', measureButtonWidth);
    };
  }, [filteredAvailableProducts.length > 0]);

  // Handle reset filters button click
  const handleResetFilters = () => {
    resetFilters(); // Reset filter state
    setFilterKey(prev => prev + 1); // Force AdvancedFilters component to remount
  };

  // Toggle advanced filters visibility without resetting filters
  const toggleAdvancedFilters = () => {
    // Simply toggle visibility without changing the filter key
    // This preserves filter state when hiding/showing the filters section
    setShowAdvancedFilters(!showAdvancedFilters);
  };

  // Show error message if any
  if (error) {
    return (
      <div className="comparison-app-container">
        <section className="page-intro">
          <h1>Compare phone models</h1>
          <p>Find the perfect smartphone for your needs by comparing different models side by side.</p>
        </section>
        <div className="error-message">
          <p><strong>Error:</strong> {error}</p>
          <p className="error-details">
            Please try again later or contact support if the problem persists.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading && shopifyProductsData.length === 0) {
    return (
      <div className="comparison-app-container">
        <section className="page-intro">
          <h1>Compare phone models</h1>
          <p>Find the perfect smartphone for your needs by comparing different models side by side.</p>
        </section>
        <div className="loading-container">
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="comparison-app-container">
      {/* Page intro section */}
      <section className="page-intro">
        <h1>Compare phone models</h1>
        <p>Find the perfect smartphone for your needs by comparing different models side by side.</p>
      </section>

      <section className="comparison-tool">
        {filteredAvailableProducts.length > 0 ? (
          <React.Fragment>
            {/* Sticky filters container */}
            <div className="sticky-filters-container">
              {/* Product filter section */}
              <ProductFilter
                availableProducts={filteredAvailableProducts}
                initialSelectedProducts={selectedProductIds}
                maxProducts={maxProducts}
                onSelectionChange={setSelectedProductIds}
                onToggleAdvancedFilters={toggleAdvancedFilters}
                onResetFilters={handleResetFilters}
                showAdvancedFilters={showAdvancedFilters}
              >
                {/* Advanced filters section - rendered as children */}
                {showAdvancedFilters && (
                  <AdvancedFilters
                    key={`advanced-filters-${filterKey}`}
                    onFilterChange={handleFilterChange}
                  />
                )}
              </ProductFilter>
            </div>

            {selectedProductIds.length > 0 && (
              <>
                {productData.length > 0 ? (
                  <ComparisonTable products={productData} />
                ) : (
                  <div className="loading-container">
                    <p>Loading product data...</p>
                  </div>
                )}
              </>
            )}
          </React.Fragment>
        ) : isLoading ? (
          // Show loading message when we're still loading products
          <div className="loading-container">
            <p>Loading available products...</p>
          </div>
        ) : (
          // Show "no results" message when filters don't match any products
          <React.Fragment>
            <div className="sticky-filters-container">
              {/* Product filter section */}
              <ProductFilter
                availableProducts={[]}
                initialSelectedProducts={selectedProductIds}
                maxProducts={maxProducts}
                onSelectionChange={setSelectedProductIds}
                onToggleAdvancedFilters={toggleAdvancedFilters}
                onResetFilters={handleResetFilters}
                showAdvancedFilters={showAdvancedFilters}
              >
                {/* Advanced filters section - keep visible when no results */}
                {showAdvancedFilters && (
                  <AdvancedFilters
                    key={`advanced-filters-${filterKey}`}
                    onFilterChange={handleFilterChange}
                  />
                )}
              </ProductFilter>
            </div>
            <div className="empty-results-message">
              <p>No models match your combination of filters. Try removing filters to start comparing models.</p>
              <button
                ref={resetButtonRef}
                className="reset-filters-button"
                onClick={handleResetFilters}
              >
                Reset Filters
              </button>
            </div>
          </React.Fragment>
        )}
      </section>
    </div>
  );
};

export default CompareApp;
